import type { ComplaintRow, ComplaintSummary } from '@/api/import';

export interface Rate {
  filled: number;
  total: number;
  percent: number | null;
}

export interface DepartmentStat {
  name: string;
  count: number;
  defect: Rate;
  reply: Rate;
  close: Rate;
}

interface MetricRow {
  deptP: string;
  defectActual: string | null;
  replyActual: string | null;
  closeActual: string | null;
}

function isFilled(value: string | null | undefined): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

function rate(filled: number, total: number): Rate {
  if (total === 0) return { filled: 0, total: 0, percent: null };
  return { filled, total, percent: Math.round((filled / total) * 1000) / 10 };
}

function summarize(rows: MetricRow[]): ComplaintSummary {
  const byDept = new Map<string, MetricRow[]>();
  for (const row of rows) {
    const list = byDept.get(row.deptP) || [];
    list.push(row);
    byDept.set(row.deptP, list);
  }
  const departments = Array.from(byDept.entries())
    .map(([name, list]: [string, MetricRow[]]) => ({
      name,
      count: list.length,
      defect: rate(list.filter((row: MetricRow) => isFilled(row.defectActual)).length, list.length),
      reply: rate(list.filter((row: MetricRow) => isFilled(row.replyActual)).length, list.length),
      close: rate(list.filter((row: MetricRow) => isFilled(row.closeActual)).length, list.length),
    }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, 'zh'));

  return {
    total: rows.length,
    defect: rate(rows.filter((row) => isFilled(row.defectActual)).length, rows.length),
    reply: rate(rows.filter((row) => isFilled(row.replyActual)).length, rows.length),
    close: rate(rows.filter((row) => isFilled(row.closeActual)).length, rows.length),
    departments,
  };
}

export function summarizeComplaintRows(rows: ComplaintRow[]): ComplaintSummary {
  return summarize(
    rows.map((row) => ({
      deptP: row.deptP,
      defectActual: row.defectActual,
      replyActual: row.replyActual,
      closeActual: row.closeActual,
    })),
  );
}
